import { useTranslation } from 'react-i18next';

import { Button, TextField } from '@mui/material';

import { Pencil } from 'lucide-react';

import ToolbarLayout from '../../components/ToolbarLayout';
import { ALL_DEVICES_ID, GroupDropdown } from '../../features/Realtime';
import AddIcon from '../../images/add.svg?react';
import ExportIcon from '../../images/export.svg?react';

const getActionButtonSx = (isMobile: any) => ({
    width: isMobile ? '100%' : 'auto',
    minWidth: isMobile ? 0 : '108px',
    height: isMobile ? '40px' : '48px',
    px: isMobile ? 1.5 : 2.5,
    borderRadius: '999px',
    backgroundColor: '#3F8EE8',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    textTransform: 'none',
    '& .MuiButton-startIcon': {
        marginLeft: 0,
        marginRight: '8px',
        flexShrink: 0,
    },
    '& .MuiButton-label': {
        whiteSpace: 'nowrap',
    },
    '&:hover': { backgroundColor: '#3F8EE8' },
    // Disabled = a solid neutral grey with a white glyph — not the brand blue faded out.
    '&.Mui-disabled': {
        backgroundColor: '#9CA3AF',
        color: '#FFFFFF',
    },
    // Override the global `button:disabled .svgIcon path` rule (Pending.css) that would
    // otherwise fade the add icon to translucent black; keep it crisp white instead.
    '&.Mui-disabled .svgIcon path': {
        fill: '#FFFFFF',
        fillOpacity: 1,
    },
});

const getToggleOptionClass = (isActive: any) =>
    `flex-1 cursor-pointer whitespace-nowrap rounded-full px-5 py-1.5 text-sm font-semibold transition-colors md:flex-none ${
        isActive ? 'bg-blue-500 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
    }`;

function RealtimeToolbarSection({
    isMobile,
    activeTab,
    setActiveTab,
    searchString,
    setSearchString,
    onSearch,
    onOpenExport,
    onOpenAdd,
    groups,
    selectedGroupId,
    onSelectGroup,
    onCreateGroup,
    onRenameGroup,
    onDeleteGroup,
}: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime' });
    // In a custom group the button manages membership, so present it as "Edit"; in
    // All Devices it registers a new device, so keep it as "Add".
    const inCustomGroup = selectedGroupId && selectedGroupId !== ALL_DEVICES_ID;
    return (
        <ToolbarLayout
            isMobile={isMobile}
            actions={[
                <Button
                    key='export'
                    variant='contained'
                    startIcon={<ExportIcon className='svgIcon' />}
                    onClick={onOpenExport}
                    sx={getActionButtonSx(isMobile)}
                    fullWidth={isMobile}
                >
                    {t('export')}
                </Button>,
                <Button
                    key='add'
                    variant='contained'
                    startIcon={inCustomGroup ? <Pencil size={18} /> : <AddIcon className='svgIcon' />}
                    onClick={onOpenAdd}
                    // Archive is a read-only view of hidden devices — no device to add and
                    // no group to edit there (the group dropdown is likewise locked).
                    disabled={activeTab === 'archive'}
                    sx={getActionButtonSx(isMobile)}
                    fullWidth={isMobile}
                >
                    {inCustomGroup ? t('edit') : t('add')}
                </Button>,
            ]}
            filterSlot={
                <div className='flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:gap-3'>
                    <div className='inline-flex w-full items-center gap-1 rounded-full bg-slate-100 p-1 md:w-auto'>
                        <button
                            type='button'
                            onClick={() => setActiveTab('active')}
                            className={getToggleOptionClass(activeTab === 'active')}
                        >
                            {t('active')}
                        </button>
                        <button
                            type='button'
                            onClick={() => setActiveTab('archive')}
                            className={getToggleOptionClass(activeTab === 'archive')}
                        >
                            {t('archive')}
                        </button>
                    </div>
                    <GroupDropdown
                        groups={groups}
                        selectedGroupId={selectedGroupId}
                        onSelect={onSelectGroup}
                        onCreate={onCreateGroup}
                        onRename={onRenameGroup}
                        onDelete={onDeleteGroup}
                        disabled={activeTab === 'archive'}
                    />
                </div>
            }
            searchInput={
                isMobile ? (
                    <TextField
                        label=''
                        variant='outlined'
                        InputProps={{ sx: { height: 40, paddingLeft: '15px' } }}
                        fullWidth
                        placeholder={t('searchPlaceholder')}
                        value={searchString}
                        onChange={(e: any) => setSearchString(e.target.value)}
                        onKeyDown={(e: any) => e.key === 'Enter' && onSearch()}
                    />
                ) : (
                    <input
                        className='flex-1 cursor-text border-none bg-transparent text-base text-slate-700 placeholder:text-slate-400 focus:outline-none'
                        placeholder={t('searchPlaceholder')}
                        value={searchString}
                        onChange={(e: any) => setSearchString(e.target.value)}
                        onKeyDown={(e: any) => e.key === 'Enter' && onSearch()}
                    />
                )
            }
            searchButton={
                isMobile ? (
                    <Button
                        variant='text'
                        disableElevation
                        onClick={onSearch}
                        className='flex items-center justify-center'
                        sx={{
                            minWidth: '80px',
                            width: '80px',
                            height: '32px',
                            borderRadius: 0,
                            borderLeft: '1px solid #D4DCEB',
                            fontWeight: 600,
                            fontSize: '15px',
                            color: '#757575',
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: 'transparent', boxShadow: 'none' },
                        }}
                    >
                        {t('search')}
                    </Button>
                ) : (
                    <button
                        type='button'
                        className='ml-4 cursor-pointer text-sm font-semibold uppercase tracking-wide text-slate-700'
                        onClick={onSearch}
                    >
                        {t('search')}
                    </button>
                )
            }
        />
    );
}

export default RealtimeToolbarSection;
